import { useCallback, useEffect, useRef } from 'react';
import type { FieldPath, FieldValues, UseFormSubscribe } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';

export type UseTrackedFieldChangesOptions<
    TFieldValues extends FieldValues,
    TFieldName extends FieldPath<TFieldValues>,
> = {
    /** The form's `subscribe` from `useForm` / `useFormExtended`. */
    subscribe: UseFormSubscribe<TFieldValues>;
    /**
     * Fields to report. Pass a stable (e.g. module-level) array — a new array
     * each render re-subscribes the form. A field also matches nested paths
     * below it, so tracking `'rebate'` catches `rebate.0.percent` edits.
     */
    fields: readonly TFieldName[];
    /**
     * Called once per tracked field the user changed within a debounce window.
     * Only user edits are reported: programmatic `reset()` and initial value
     * population emit no `change` event, so they're ignored.
     */
    onFieldChange: (field: TFieldName) => void;
    debounceDelay?: number;
};

/**
 * Reports which tracked form fields a user edited, debounced and de-duplicated.
 * Built for firing analytics events on field changes without the caller having
 * to diff values or filter out programmatic resets.
 */
export function useTrackedFieldChanges<
    TFieldValues extends FieldValues,
    TFieldName extends FieldPath<TFieldValues>,
>({
    subscribe,
    fields,
    onFieldChange,
    debounceDelay = 1000,
}: UseTrackedFieldChangesOptions<TFieldValues, TFieldName>): void {
    const pendingFields = useRef(new Set<TFieldName>());

    // Keep the latest callback in a ref so an inline `onFieldChange` doesn't
    // re-subscribe the form on every render.
    const onFieldChangeRef = useRef(onFieldChange);
    useEffect(() => {
        onFieldChangeRef.current = onFieldChange;
    }, [onFieldChange]);

    const flush = useDebounceCallback(
        useCallback(() => {
            const changed = [...pendingFields.current];
            pendingFields.current.clear();
            for (const field of changed) {
                onFieldChangeRef.current(field);
            }
        }, []),
        debounceDelay,
    );

    useEffect(() => {
        return subscribe({
            formState: { values: true, dirtyFields: true },
            callback: ({ name, type, dirtyFields }) => {
                // Only user edits carry a `change` type and a field `name`;
                // programmatic resets and initial population do not.
                if (type !== 'change' || !name) {
                    return;
                }

                // Match the field itself or any nested path below it (e.g. a
                // `rebate.0.percent` edit counts as a change to `rebate`).
                const field = fields.find(
                    (candidate) => name === candidate || name.startsWith(`${candidate}.`),
                );

                if (!field) {
                    return;
                }

                if (dirtyFields && field in dirtyFields) {
                    pendingFields.current.add(field);
                    flush();
                }
            },
        });
    }, [fields, flush, subscribe]);
}
